'use client';

import { useFlags } from 'launchdarkly-react-client-sdk';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import qs from 'qs';
import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { shallow } from 'zustand/shallow';

import { CAR_TRANSFER_FEE_NSW, PLATE_TRANSFER_FEE, PROMO_4, TRADE_IN_FORM_PATH } from 'src/constants';
import { productDetailPayloadToBaseCarData } from 'src/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { getCarDetail } from 'src/data/StaticPageGeneration/PagePdpDefaults';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { IntersectionObserverHandler } from 'src/general/components/IntersectionObserverHandler/IntersectionObserverHandler';
import { processPriceString } from 'src/general/helpers/processProductApiData';
import { CarGallery } from 'src/pdp/components/CarGallery/CarGallery';
import { CarHero } from 'src/pdp/components/CarHero/CarHero';
import { CarOverview } from 'src/pdp/components/CarOverview/CarOverview';
import { CarPriceCard } from 'src/pdp/components/CarPriceCard/CarPriceCard';
import { CarmaCoreValue } from 'src/pdp/components/CarmaCoreValueProps/CarmaCoreValue';
import { CartCarSwitchModal } from 'src/pdp/components/CartCarSwitchModal/CartCarSwitchModal';
import {
  FeatureModal,
  TAB_VALUE_FEATURES,
  TAB_VALUE_SPECIFICATIONS,
} from 'src/pdp/components/FeatureModal/FeatureModal';
import { ModalPurchaseInProgress } from 'src/pdp/components/ModalPurchaseInProgress/ModalPurchaseInProgress';
import { ServiceHistory } from 'src/pdp/components/ServiceHistory/ServiceHistory';
import { TradeIn, TradeInFormData } from 'src/pdp/components/TradeIn/TradeIn';
import { PdpLayout } from 'src/pdp/layouts/PdpLayout/PdpLayout';
import { useUpdatedCarStatus } from 'src/plp/components/Results/useUpdatedCarStatus';
import { authStore } from 'src/stores/authStore';
import { cartStore } from 'src/stores/cartStore';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { ProductDetailsPayload } from 'src/types/ProductDetail.type';
import {
  DataLayerPdpEvent,
  baseCarDataToDataLayerItem,
  productDetailToDataLayer,
  productDetailToListDataRow,
  pushToDataLayer,
  pushToDataLayerPDP,
} from 'src/utils/pushToDataLayer';

import { debounce, throttle } from 'lodash';
import { useFeatureHotspotsStore } from 'src/stores/featureHotspotsStore';
import { Features } from '../Features/Features';
import { Specs } from '../Specs/Specs';
import {
  CarDisplayState,
  formatCarDisplayState,
  formatCarGallery,
  formatFeatures,
  formatFeaturesWithSorted,
  formatSpecs,
  getGalleryDisplayImageIndex,
  getTargetViewType,
} from './pdpHelpers';

const ModalGovernmentCharges = dynamic(
  () =>
    import('src/general/components/ModalGovernmentCharges/ModalGovernmentCharges').then(
      (mod) => mod.ModalGovernmentCharges,
    ),
  { ssr: false },
);

const CarEnquiryFormWrapper = dynamic(
  () =>
    import('src/pdp/components/CarEnquiryFormWrapper/CarEnquiryFormWrapper').then((mod) => mod.CarEnquiryFormWrapper),
  { ssr: false },
);

const ExpandedGalleryViewFrameModal = dynamic(
  () =>
    import('src/pdp/components/ExpandedGalleryViewFrame/ExpandedGalleryViewFrameModal').then(
      (mod) => mod.ExpandedGalleryViewFrameModal,
    ),
  { ssr: false },
);

const CarStickyBar = dynamic(
  () => import('src/pdp/components/CarStickyBar/CarStickyBar').then((mod) => mod.CarStickyBar),
  { ssr: false },
);

type GalleryDisplay = 'view360' | 'gallery' | 'imperfections';
export type PdpModalOpen = 'governmentCharges' | 'purchaseInProgress' | null;

export interface GalleryModalState {
  displayState: GalleryDisplay;
  openDoors: boolean;
  exterior: boolean;
  galleryStartIndex?: number;
}

interface Props {
  sku: string;
  productDetails: ProductDetailsPayload;
  statusPill: ReactNode;
  promoPill: ReactNode;
  // Temporary prop drilling to be able to use helper modal in Imperfections, refactoring needed
  helperModals?: {
    imperfectionCriteria?: ReactNode;
  };
}

const PRICE_CARD_ID = 'priceCard';
const ENQUIRY_FORM_ID = 'carEnquiryForm';

export const Pdp: FC<Props> = ({ sku, productDetails, statusPill, promoPill, helperModals }) => {
  const flags = useFlags();
  const searchParams = useSearchParams();
  const action = searchParams?.get('action');
  const showCartCarSwitch = action === 'switchCart';
  const { isUserLoggedIn, userOrdersLoading, userOrders } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
      userOrdersLoading: state.userOrdersLoading,
      userOrders: state.userOrders,
    }),
    shallow,
  );
  const { carLoaded, carData, cartExpired, cartExpiry, apiCartData, apiCartLoaded } = cartStore(
    (state) => ({
      carLoaded: state.carLoaded,
      carData: state.carData,
      cartExpired: state.cartExpired,
      cartExpiry: state.cartExpiry,
      apiCartData: state.apiCartData,
      apiCartLoaded: state.apiCartLoaded,
    }),
    shallow,
  );
  const [isOpen, setIsOpen] = useState(false);
  // Load asynchronously the car data to handle statuses update
  const { data: dynamicProductDetails } = useSWR(sku, getCarDetail);
  const [carDisplayState, setCarDisplayState] = useState<CarDisplayState>('loading');
  const [galleryModalState, setGalleryModalState] = useState<GalleryModalState | null>(null);
  const [displayMode, setDisplayMode] = useState<'doorsOpen' | 'doorsClosed' | 'interior'>('doorsClosed');
  const [modalOpen, setModalOpen] = useState<PdpModalOpen>(null);
  const [featureModalState, setFeatureModalState] = useState<{
    isOpen: boolean;
    initialTab: typeof TAB_VALUE_FEATURES | typeof TAB_VALUE_SPECIFICATIONS;
  }>({ isOpen: false, initialTab: TAB_VALUE_FEATURES });
  const carStatus = useUpdatedCarStatus(sku, productDetails.state);

  const { onClickFeatureHotspot, setViewType } = useFeatureHotspotsStore();

  const shouldShowCartSwitch = useMemo(
    () => showCartCarSwitch && carLoaded && !!carData && !cartExpired,
    [carLoaded, carData, cartExpired],
  );

  const isCheckoutProgress = useMemo(() => carLoaded && !!carData && !cartExpired, [carLoaded, carData, cartExpired]);

  const router = useRouter();
  const navigateToTradeIn = useCallback(
    (tradeInSearchData: TradeInFormData) => {
      pushToDataLayer({ event: 'pdp_trade_in_start' });

      router.push(`${TRADE_IN_FORM_PATH}?${qs.stringify({ ...tradeInSearchData, sku, searchType: 'byRego' })}`);
    },
    [router, sku, flags],
  );

  const onOpenCarEnquiryForm = (entryPoint: string) => {
    setIsOpen(true);
    pushToDataLayer({
      event: 'lead_form_start',
      ecommerce: {
        items: [productDetailToDataLayer(productDetails)],
      },
      entry_point: entryPoint,
    });
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onDisplayGovtCharges = useCallback(() => {
    setModalOpen('governmentCharges');
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku,
      option: 'PRICE_GUIDE',
    });
  }, [setModalOpen]);

  const onCloseModal = useCallback(() => {
    setModalOpen(null);
  }, [setModalOpen]);

  useMemo(() => {
    const carDisplayState = formatCarDisplayState(
      apiCartData,
      userOrders,
      isUserLoggedIn,
      apiCartLoaded && !userOrdersLoading,
      dynamicProductDetails,
    );
    setCarDisplayState(carDisplayState);
    if (carDisplayState === 'not-available' && (carStatus === 'draftOrder' || carStatus === 'reserved')) {
      setModalOpen('purchaseInProgress');
    } else {
      setModalOpen(null);
    }
  }, [
    dynamicProductDetails,
    apiCartData,
    isUserLoggedIn,
    userOrdersLoading,
    userOrders,
    apiCartLoaded,
    setModalOpen,
    carStatus,
    carDisplayState,
  ]);

  const openGalleryModalGallery = useCallback(() => {
    setGalleryModalState({
      displayState: 'gallery',
      openDoors: false,
      exterior: false,
      galleryStartIndex: getGalleryDisplayImageIndex(productDetails),
    });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'GALLERY',
    });
  }, [productDetails, setGalleryModalState]);

  const openGalleryModalImperfection = useCallback(() => {
    setGalleryModalState({
      displayState: 'imperfections',
      openDoors: false,
      exterior: false,
    });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'IMPERFECTIONS',
    });
  }, [productDetails, setGalleryModalState]);

  const onExpand = useCallback(() => {
    setGalleryModalState({
      displayState: 'view360',
      openDoors: displayMode === 'doorsOpen',
      exterior: displayMode !== 'interior',
    });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'EXPAND',
    });
  }, [productDetails, displayMode]);

  const closeGalleryModal = useCallback(() => {
    setGalleryModalState(null);
  }, [setGalleryModalState]);

  // This fixes an issue where on android the page would go into an infinite loop
  const throttledCloseGalleryModal = throttle(closeGalleryModal, 200);

  const onShowInteriorMobileGallery = useCallback(() => {
    setGalleryModalState({
      displayState: 'view360',
      openDoors: displayMode === 'doorsOpen',
      exterior: false,
    });
    pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
      sku: productDetails.sku,
      option: 'INTERIOR',
    });
  }, [setDisplayMode]);

  const handleFeatureHotspotClick = throttle(
    (featureName: string, section: 'exterior' | 'interior') => {
      setGalleryModalState({
        displayState: 'view360',
        openDoors: false,
        exterior: section === 'exterior',
        galleryStartIndex: getGalleryDisplayImageIndex(productDetails),
      });
      setViewType(section);
      onClickFeatureHotspot(featureName);
      pushToDataLayer({
        event: 'pdp_cta_interaction',
        value: {
          option: 'HOTSPOT',
          type: 'CLICK',
          value: featureName,
        },
      });
    },
    200,
    { trailing: false },
  );

  const handleInteriorFeatureHotspotClick = useCallback(
    (featureName: string) => {
      onClickFeatureHotspot(featureName);
      pushToDataLayer({
        event: 'pdp_gallery_interaction',
        value: {
          option: 'HOTSPOT',
          type: 'CLICK',
          value: featureName,
        },
      });
    },
    [onClickFeatureHotspot],
  );

  const onExpandServiceHistory = () => {
    pushToDataLayer({
      event: 'pdp_interaction',
      type: 'accordion',
      value: 'view_service_history',
    });
  };

  const onViewAllFeatures = () => {
    setFeatureModalState({ isOpen: true, initialTab: TAB_VALUE_FEATURES });
  };
  const onViewAllSpecs = () => {
    setFeatureModalState({ isOpen: true, initialTab: TAB_VALUE_SPECIFICATIONS });
  };

  useEffect(() => {
    pushToDataLayer({ ecommerce: null });
    pushToDataLayer({
      event: 'view_item',
      ecommerce: {
        items: [baseCarDataToDataLayerItem(productDetailPayloadToBaseCarData(productDetails))],
      },
    });
    setViewType('exterior');
  }, []);

  useEffect(() => {
    if (flags?.halloweenPdpHotspots) {
      import('src/general/helpers/flomingoAscii').then(({ flomingoAsciiInConsole }) => flomingoAsciiInConsole());
    }
  }, [flags?.halloweenPdpHotspots]);

  const product = productDetailToListDataRow(productDetails);

  // This appears to fix an issue specifically on android devices where changing the display mode to interior causes the page to crash
  const debouncedSetDisplayMode = useCallback(
    debounce((mode) => {
      setDisplayMode(mode);
    }, 300),
    [setDisplayMode],
  );

  const onChangeDisplayMode = useCallback(
    (mode: 'doorsOpen' | 'doorsClosed' | 'interior') => {
      debouncedSetDisplayMode(mode);
    },
    [setDisplayMode],
  );

  return (
    <>
      <PdpLayout
        hero={
          <CarHero
            sku={sku}
            product={product}
            images={productDetails.images}
            statusPill={statusPill}
            promoPill={promoPill}
            carState={carStatus}
            onExpand={onExpand}
            onFeatureHotspotClick={handleFeatureHotspotClick}
            onChangeDisplayMode={onChangeDisplayMode}
            hasPromo={productDetails.search_keywords?.includes(PROMO_4)}
          />
        }
        price={
          <>
            <CarPriceCard
              id={PRICE_CARD_ID}
              productDetails={productDetails}
              displayState={carDisplayState}
              isCheckoutProgress={isCheckoutProgress}
              onDisplayGovtCharges={onDisplayGovtCharges}
              onOpenCarEnquiryForm={onOpenCarEnquiryForm}
            />
            <CarmaCoreValue />
          </>
        }
        gallery={
          <CarGallery
            {...formatCarGallery(
              productDetails,
              openGalleryModalGallery,
              openGalleryModalImperfection,
              onShowInteriorMobileGallery,
            )}
          />
        }
      >
        <TradeIn onSubmit={navigateToTradeIn} sku={sku} />
        <CarOverview {...productDetails} />
        <IntersectionObserverHandler
          margins="-50%"
          onIntersecting={() => {
            pushToDataLayer({
              event: 'pdp_interaction',
              type: 'scroll',
              value: 'specs_and_features',
            });
          }}
        >
          <Features {...formatFeatures(productDetails)} onViewAllFeatures={onViewAllFeatures} />
          <Specs {...formatSpecs(productDetails)} onViewAllSpecs={onViewAllSpecs} />
        </IntersectionObserverHandler>
        <ServiceHistory serviceHistory={productDetails.serviceHistory} onDataEvent={onExpandServiceHistory} />
        <CarEnquiryFormWrapper
          isOpen={isOpen}
          productDetailsPayload={productDetails}
          onClose={onClose}
          formId={ENQUIRY_FORM_ID}
        />
      </PdpLayout>
      <CarStickyBar
        priceCardId={PRICE_CARD_ID}
        formId={ENQUIRY_FORM_ID}
        isCheckoutProgress={isCheckoutProgress}
        productDetails={productDetails}
        displayState={carDisplayState}
        onOpenCarEnquiryForm={onOpenCarEnquiryForm}
      />
      {/* Modals */}
      {!!galleryModalState && (
        <ExpandedGalleryViewFrameModal
          productDetails={productDetails}
          isOpen={!!galleryModalState}
          defaultViewType={getTargetViewType(galleryModalState)}
          onClose={throttledCloseGalleryModal}
          handleInteriorFeatureHotspotClick={handleInteriorFeatureHotspotClick}
          helperModals={helperModals}
        />
      )}
      <ModalGovernmentCharges
        isOpen={modalOpen === 'governmentCharges'}
        onClose={onCloseModal}
        header={getMicrocopy(MICROCOPY.GENERAL_GOVERNMENT_CHARGES_MODAL_TITLE)}
        description={getMicrocopy(MICROCOPY.GENERAL_GOVERNMENT_CHARGES_MODAL_NOTE)}
        carPrice={processPriceString(productDetails.price)}
        dutyPrice={processPriceString(productDetails.dutyPrice)}
        transferFee={CAR_TRANSFER_FEE_NSW}
        plateTransferFee={PLATE_TRANSFER_FEE}
      />
      <ModalPurchaseInProgress
        isOpen={modalOpen === 'purchaseInProgress'}
        onClose={onCloseModal}
        similarCarsUrl={`/product-detail/${productDetails.sku}/similar`}
        carPurchaseStatus={carStatus}
        addToWatchlistProps={{
          sku: productDetails.sku,
          product,
        }}
      />
      {shouldShowCartSwitch && (
        <CartCarSwitchModal
          carCart={carData!}
          car={productDetailPayloadToBaseCarData(productDetails)}
          cartExpiry={cartExpiry}
        />
      )}
      {featureModalState.isOpen && (
        <FeatureModal
          onClose={() => setFeatureModalState({ isOpen: false, initialTab: TAB_VALUE_FEATURES })}
          {...formatFeaturesWithSorted(productDetails)}
          {...formatSpecs(productDetails)}
          initialTab={featureModalState.initialTab}
        />
      )}
    </>
  );
};
